/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

/* ─── Low-level hook (still usable standalone) ─── */

/**
 * Returns `isSlow: true` only after `delay` ms while `isPending` remains true.
 * If the operation finishes before the delay, `isSlow` never becomes true.
 * No minimum display time — clears immediately when isPending becomes false.
 */
export function useSlowTransaction(
  isPending: boolean,
  options?: { delay?: number },
): { isSlow: boolean } {
  const delay = options?.delay ?? 400;
  const [isSlow, setIsSlow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isPending) {
      timerRef.current = setTimeout(() => setIsSlow(true), delay);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      setIsSlow(false);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isPending, delay]);

  return { isSlow };
}

/* ─── Global overlay context ─── */

interface SlowOverlayContextValue {
  register: (id: string) => void;
  unregister: (id: string) => void;
  /** True when at least one registered slow transaction is active */
  isVisible: boolean;
}

const SlowOverlayContext = createContext<SlowOverlayContextValue | null>(null);

export { SlowOverlayContext };

/**
 * Provider that manages the global slow-transaction overlay state.
 * Wrap your app with this. Renders children + the overlay when active.
 *
 * Usage: <SlowOverlayProvider>{children}</SlowOverlayProvider>
 */
export function SlowOverlayProvider({ children }: { children: ReactNode }) {
  const [activeIds, setActiveIds] = useState<Set<string>>(new Set());

  const register = useCallback((id: string) => {
    setActiveIds((prev) => {
      if (prev.has(id)) return prev;        // no-op: avoid new reference
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setActiveIds((prev) => {
      if (!prev.has(id)) return prev;       // no-op: avoid new reference
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const isVisible = activeIds.size > 0;

  const contextValue = useMemo(
    () => ({ register, unregister, isVisible }),
    [register, unregister, isVisible],
  );

  return (
    <SlowOverlayContext.Provider value={contextValue}>
      {children}
    </SlowOverlayContext.Provider>
  );
}

let _overlayIdCounter = 0;

/**
 * One-liner hook: registers a slow transaction with the global overlay.
 * When `isPending` stays true for longer than `delay` ms, the global
 * CoreXLoader overlay appears. No JSX needed in the calling component.
 *
 * Usage:
 *   useSlowOverlay(loginMutation.isPending);
 *   useSlowOverlay(saveQuery.isLoading, { delay: 600 });
 */
export function useSlowOverlay(
  isPending: boolean,
  options?: { delay?: number },
) {
  const ctx = useContext(SlowOverlayContext);
  const { isSlow } = useSlowTransaction(isPending, options);
  const idRef = useRef(`slow-overlay-${++_overlayIdCounter}`);

  useEffect(() => {
    if (!ctx) return;
    if (isSlow) {
      ctx.register(idRef.current);
    } else {
      ctx.unregister(idRef.current);
    }
  }, [isSlow, ctx]);

  // Cleanup on unmount
  useEffect(() => {
    const id = idRef.current;
    return () => {
      ctx?.unregister(id);
    };
  }, [ctx]);
}
